import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Popover,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  Chip
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import SaveIcon from '@mui/icons-material/Save';
import ClearIcon from '@mui/icons-material/Clear';
import SettingsIcon from '@mui/icons-material/Settings';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';

const HEADER_MIN_HEIGHT = 64;
const HEADER_MAX_HEIGHT = 132;
const HEADER_HORIZONTAL_PADDING = 28;
const HEADER_AVERAGE_CHAR_WIDTH = 7;
const HEADER_LINE_HEIGHT = 17;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getHeaderText = (column) => String(column.headerName || column.field || '');

const getColumnWidth = (column) => {
  const width = Number(column.width || column.minWidth || 120);
  return Number.isFinite(width) ? width : 120;
};

const estimateHeaderLineCount = (header, width) => {
  const availableChars = Math.max(8, Math.floor((width - HEADER_HORIZONTAL_PADDING) / HEADER_AVERAGE_CHAR_WIDTH));
  const words = header.split(/\s+/).filter(Boolean);
  if (words.length === 0) return 1;

  let lines = 1;
  let currentLineLength = 0;

  words.forEach((word) => {
    if (word.length > availableChars) {
      lines += Math.ceil(word.length / availableChars) - (currentLineLength === 0 ? 1 : 0);
      currentLineLength = word.length % availableChars;
      return;
    }

    const nextLineLength = currentLineLength === 0
      ? word.length
      : currentLineLength + 1 + word.length;

    if (nextLineLength <= availableChars) {
      currentLineLength = nextLineLength;
    } else {
      lines += 1;
      currentLineLength = word.length;
    }
  });

  return lines;
};

const getHeaderColumnWidth = (column) => {
  const header = getHeaderText(column);
  const currentWidth = getColumnWidth(column);

  if (!header || column.flex || column.field === 'actions') {
    return currentWidth;
  }

  const recommendedWidth = clamp(header.length * 4.5 + 56, currentWidth, 168);
  return Math.round(recommendedWidth);
};

/**
 * Enhanced Data Table Component with advanced features:
 * - Responsive design that doesn't truncate data
 * - Customizable column visibility
 * - User preference saving
 * - Advanced filtering
 * - Column reordering
 * - Saved views
 */
const EnhancedDataTable = React.forwardRef(({
  rows,
  columns,
  loading = false,
  title = '',
  tableName = 'default',
  onRowClick = null,
  height = 600,
  // defaultPageSize removed as it's not needed in Pro version
  checkboxSelection = false,
  disableSelectionOnClick = true,
  onSelectionModelChange = null,
  selectionModel = [],
  hideFooter = true,
  disableColumnMenu = false,
  disableColumnFilter = false,
  disableColumnSelector = false,
  disableDensitySelector = false,
  disableToolbar = false,
  disableColumnReorder = false,
  disableColumnSorting = false,
  additionalToolbarContent = null,
  getRowClassName = null,
  pinnedColumns = { right: ['actions'] },
  columnHeaderHeight = 50
}, ref) => {
  // State for column visibility
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [savedViews, setSavedViews] = useState([]);
  const [currentView, setCurrentView] = useState(null);
  const [viewAnchorEl, setViewAnchorEl] = useState(null);
  const [saveViewDialogOpen, setSaveViewDialogOpen] = useState(false);
  const [newViewName, setNewViewName] = useState('');

  // Load user preferences from localStorage on component mount
  useEffect(() => {
    const savedPreferences = localStorage.getItem(`table-prefs-${tableName}`);
    if (savedPreferences) {
      const prefs = JSON.parse(savedPreferences);
      setColumnVisibilityModel(prefs.columnVisibility || {});
    }

    const savedTableViews = localStorage.getItem(`table-views-${tableName}`);
    if (savedTableViews) {
      setSavedViews(JSON.parse(savedTableViews));
    }
  }, [tableName]);

  // Process columns to auto-pin the actions column to the right and handle sorting
  const processedColumns = React.useMemo(() => {
    return columns.map(column => {
      const headerWidth = getHeaderColumnWidth(column);
      const widthAdjustedColumn = column.flex || column.field === 'actions'
        ? { ...column, headerAlign: 'center' }
        : {
            ...column,
            headerAlign: 'center',
            width: Math.max(getColumnWidth(column), headerWidth),
            minWidth: Math.max(Number(column.minWidth || 0), headerWidth)
          };

      if (column.field === 'actions') {
        return { ...widthAdjustedColumn, pinned: 'right' };
      }
      // Disable sorting for all columns if disableColumnSorting is true
      if (disableColumnSorting) {
        return { ...widthAdjustedColumn, sortable: false };
      }
      return widthAdjustedColumn;
    });
  }, [columns, disableColumnSorting]);

  const effectiveColumnHeaderHeight = React.useMemo(() => {
    const maxHeaderLines = processedColumns.reduce((maxLines, column) => {
      const header = getHeaderText(column);
      const width = getColumnWidth(column);
      return Math.max(maxLines, estimateHeaderLineCount(header, width));
    }, 1);

    const calculatedHeight = 28 + maxHeaderLines * HEADER_LINE_HEIGHT;
    const minimumHeight = maxHeaderLines > 1 ? HEADER_MIN_HEIGHT : columnHeaderHeight;

    return clamp(
      Math.max(columnHeaderHeight, calculatedHeight, minimumHeight),
      minimumHeight,
      HEADER_MAX_HEIGHT
    );
  }, [columnHeaderHeight, processedColumns]);

  // Handle column visibility menu open
  const handleColumnVisibilityClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle column visibility menu close
  const handleColumnVisibilityClose = () => {
    setAnchorEl(null);
  };

  // Handle column visibility change
  const handleColumnVisibilityChange = (event) => {
    const { name, checked } = event.target;
    const newVisibilityModel = {
      ...columnVisibilityModel,
      [name]: checked
    };
    setColumnVisibilityModel(newVisibilityModel);
    
    // Save to localStorage
    const savedPreferences = localStorage.getItem(`table-prefs-${tableName}`) || '{}';
    const prefs = JSON.parse(savedPreferences);
    localStorage.setItem(`table-prefs-${tableName}`, JSON.stringify({
      ...prefs,
      columnVisibility: newVisibilityModel
    }));
  };

  // Handle saved views menu open
  const handleViewMenuOpen = (event) => {
    setViewAnchorEl(event.currentTarget);
  };

  // Handle saved views menu close
  const handleViewMenuClose = () => {
    setViewAnchorEl(null);
  };

  // Handle save view dialog open
  const handleSaveViewClick = () => {
    setSaveViewDialogOpen(true);
    setViewAnchorEl(null);
  };

  // Handle save view
  const handleSaveView = () => {
    if (!newViewName.trim()) return;

    const newView = {
      id: Date.now(),
      name: newViewName,
      columnVisibility: columnVisibilityModel,
      sortModel: [], // Add sorting model if implemented
      filterModel: {} // Add filter model if implemented
    };

    const updatedViews = [...savedViews, newView];
    setSavedViews(updatedViews);
    setCurrentView(newView);
    setNewViewName('');
    setSaveViewDialogOpen(false);

    // Save to localStorage
    localStorage.setItem(`table-views-${tableName}`, JSON.stringify(updatedViews));
  };

  // Handle load view
  const handleLoadView = (view) => {
    setColumnVisibilityModel(view.columnVisibility || {});
    setCurrentView(view);
    setViewAnchorEl(null);

    // Save current preferences to localStorage
    const savedPreferences = localStorage.getItem(`table-prefs-${tableName}`) || '{}';
    const prefs = JSON.parse(savedPreferences);
    localStorage.setItem(`table-prefs-${tableName}`, JSON.stringify({
      ...prefs,
      columnVisibility: view.columnVisibility || {}
    }));
  };

  // Handle delete view
  const handleDeleteView = (viewId) => {
    const updatedViews = savedViews.filter(view => view.id !== viewId);
    setSavedViews(updatedViews);
    
    if (currentView && currentView.id === viewId) {
      setCurrentView(null);
    }

    // Save to localStorage
    localStorage.setItem(`table-views-${tableName}`, JSON.stringify(updatedViews));
  };

  // Expose methods to parent component via ref
  React.useImperativeHandle(ref, () => ({
    handleColumnVisibilityClick,
    handleViewMenuOpen
  }));

  // Column visibility menu
  const columnVisibilityMenu = (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={handleColumnVisibilityClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <Box sx={{ p: 2, width: 250 }}>
        <Typography variant="subtitle1" gutterBottom>
          Column Visibility
        </Typography>
        <Divider sx={{ mb: 1 }} />
        <FormGroup>
          {columns.map((column) => (
            <FormControlLabel
              key={column.field}
              control={
                <Checkbox
                  checked={columnVisibilityModel[column.field] !== false}
                  onChange={handleColumnVisibilityChange}
                  name={column.field}
                />
              }
              label={column.headerName}
            />
          ))}
        </FormGroup>
      </Box>
    </Popover>
  );

  // Saved views menu
  const savedViewsMenu = (
    <Menu
      anchorEl={viewAnchorEl}
      open={Boolean(viewAnchorEl)}
      onClose={handleViewMenuClose}
    >
      <MenuItem onClick={handleSaveViewClick}>
        <SaveIcon fontSize="small" sx={{ mr: 1 }} />
        Save Current View
      </MenuItem>
      <Divider />
      {savedViews.length > 0 ? (
        savedViews.map(view => (
          <MenuItem key={view.id} onClick={() => handleLoadView(view)}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {currentView && currentView.id === view.id ? (
                  <BookmarkIcon fontSize="small" sx={{ mr: 1 }} />
                ) : (
                  <BookmarkBorderIcon fontSize="small" sx={{ mr: 1 }} />
                )}
                {view.name}
              </Box>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteView(view.id);
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </Box>
          </MenuItem>
        ))
      ) : (
        <MenuItem disabled>No saved views</MenuItem>
      )}
    </Menu>
  );

  // Save view dialog
  const saveViewDialog = (
    <Popover
      open={saveViewDialogOpen}
      anchorEl={viewAnchorEl}
      onClose={() => setSaveViewDialogOpen(false)}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <Box sx={{ p: 2, width: 300 }}>
        <Typography variant="subtitle1" gutterBottom>
          Save View
        </Typography>
        <TextField
          fullWidth
          label="View Name"
          value={newViewName}
          onChange={(e) => setNewViewName(e.target.value)}
          margin="normal"
          variant="outlined"
          size="small"
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button onClick={() => setSaveViewDialogOpen(false)} sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveView}>
            Save
          </Button>
        </Box>
      </Box>
    </Popover>
  );

  // Custom toolbar content
  const customToolbar = () => (
    <Box
      sx={{
        py: 1,
        px: 0, // Remove horizontal padding
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', pl: 0 }}>
        {title && (
          <Typography variant="h6" component="div" sx={{ mr: 2, pl: 0 }}>
            {title}
          </Typography>
        )}
        {currentView && (
          <Chip
            label={currentView.name}
            icon={<BookmarkIcon fontSize="small" />}
            variant="outlined"
            sx={{ ml: 1 }}
          />
        )}
      </Box>
      
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {additionalToolbarContent}
      </Box>
    </Box>
  );

  return (
    <Paper sx={{ height: height, width: '100%', overflowX: 'hidden', overflowY: 'auto', borderRadius: 0, pl: 0, display: 'flex', flexDirection: 'column' }}>
      <DataGridPro
        rows={rows}
        columns={processedColumns}
        loading={loading}
        // Pro version doesn't require pagination props
        checkboxSelection={checkboxSelection}
        disableSelectionOnClick={disableSelectionOnClick}
        onRowSelectionModelChange={(newModel) => {
          if (onSelectionModelChange) {
            onSelectionModelChange(newModel);
          }
        }}
        selectionModel={selectionModel}
        onRowClick={onRowClick}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        rowHeight={42} // set row height here
        columnHeaderHeight={effectiveColumnHeaderHeight}
        getRowClassName={getRowClassName}
        initialState={{
          pinnedColumns: pinnedColumns
        }}
        components={{
          Toolbar: customToolbar,
        }}
        componentsProps={{
          toolbar: {
            title,
            currentView,
            additionalToolbarContent,
            handleColumnVisibilityClick,
            handleViewMenuOpen,
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          }
        }}
        hideFooter={hideFooter}
        disableColumnMenu={disableColumnMenu}
        disableColumnFilter={disableColumnFilter}
        disableColumnSelector={disableColumnSelector}
        disableDensitySelector={disableDensitySelector}
        sortingOrder={disableColumnSorting ? [] : ['asc', 'desc', null]}
        disableColumnSorting={disableColumnSorting}
        sx={{
          overflow: 'auto',
          '& .MuiDataGrid-main': {
            // Remove left padding from the main grid container
            paddingLeft: 0,
          },
          // Enable scrolling for the data content area
          '& .MuiDataGrid-virtualScroller': {
            overflow: 'auto',
          },
          // Keep header fixed during scroll
          '& .MuiDataGrid-virtualScrollerContent': {
            minHeight: 'auto !important',
          },
          // Set up proper sizing for scrolling
          '& .MuiDataGrid-root': {
            border: 'none',
            paddingLeft: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
          // Keep the header fixed while scrolling
          '& .MuiDataGrid-columnHeadersInner': {
            position: 'sticky',
            top: 0,
            zIndex: 1,
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'var(--color-primary)',
            color: 'var(--color-white)',
            paddingLeft: 0, // Remove padding from column headers
            borderRadius: 0, // Square off corners of header row
            minHeight: `${effectiveColumnHeaderHeight}px !important`,
            height: `${effectiveColumnHeaderHeight}px !important`,
            maxHeight: `${effectiveColumnHeaderHeight}px !important`,
            lineHeight: '1.15 !important',
          },
          // Override the pinned column headers styling
          '& .MuiDataGrid-pinnedColumnHeaders': {
            backgroundColor: 'var(--color-primary)',
            color: 'var(--color-white)',
            boxShadow: '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)',
            minHeight: `${effectiveColumnHeaderHeight}px !important`,
            height: `${effectiveColumnHeaderHeight}px !important`,
            maxHeight: `${effectiveColumnHeaderHeight}px !important`,
            zIndex: 3,
          },
          // Left pinned column headers
          '& .MuiDataGrid-pinnedColumnHeaders--left': {
            backgroundColor: 'var(--color-primary)',
            color: 'var(--color-white)',
            zIndex: 4,
          },
          // Left pinned columns
          '& .MuiDataGrid-cell--pinnedLeft': {
            backgroundColor: 'var(--color-white)',
            zIndex: 2,
          },
          // Left pinned column headers specific
          '& .MuiDataGrid-columnHeader--pinnedLeft': {
            backgroundColor: 'var(--color-primary)',
            color: 'var(--color-white)',
            zIndex: 4,
          },
          '& .MuiDataGrid-columnSeparator': {
            visibility: 'visible', // Always show column separators
            color: 'rgba(255, 255, 255)', // Light white color for the separators
          },
          // Hide the last column separator (after Actions column)
          '& .MuiDataGrid-columnHeader:last-child .MuiDataGrid-columnSeparator': {
            visibility: 'hidden',
          },
          '& .MuiDataGrid-columnHeader': {
            paddingLeft: 0, // Remove padding from individual column header cells
            minHeight: `${effectiveColumnHeaderHeight}px !important`,
            height: `${effectiveColumnHeaderHeight}px !important`,
            maxHeight: `${effectiveColumnHeaderHeight}px !important`,
            alignItems: 'stretch',
          },
          '& .MuiDataGrid-columnHeader--alignLeft .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeader--alignCenter .MuiDataGrid-columnHeaderTitleContainer': {
            justifyContent: 'center !important',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
            paddingLeft: '12px !important',
            paddingRight: '12px !important',
            whiteSpace: 'normal !important',
            lineHeight: '1.15 !important',
            wordBreak: 'break-word !important',
            overflow: 'hidden !important',
            display: 'flex !important',
            alignItems: 'center !important',
            justifyContent: 'center !important',
            height: '100% !important',
            maxHeight: '100% !important',
            textAlign: 'center !important',
            textOverflow: 'clip !important',
          },
          '& .MuiDataGrid-columnHeaderDraggableContainer, & .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeaderTitleContainerContent': {
            height: '100% !important',
            minHeight: '100% !important',
            maxHeight: '100% !important',
            alignItems: 'stretch !important',
            justifyContent: 'center !important',
          },
          '& .MuiDataGrid-columnHeaderTitleContainer': {
            padding: '6px 0 !important',
            minWidth: 0,
          },
          '& .MuiDataGrid-columnHeaderTitleContainerContent > span': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            paddingLeft: '12px',
            paddingRight: '12px',
            textAlign: 'center',
            lineHeight: 1.15,
            whiteSpace: 'normal',
          },
          // Default styling for all cells
          '& .MuiDataGrid-cell': {
            whiteSpace: 'normal !important',
            wordWrap: 'break-word !important',
            lineHeight: '1 !important',
            padding: '0px !important',
            overflow: 'hidden !important',
          },
          // Text cells should have left padding to align with headers
          '& .MuiDataGrid-cell:not([data-field="actions"]):not([data-field=""]):not(:has(.MuiCheckbox-root))': {
            paddingLeft: '10px !important',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'var(--color-hover)',
          },
          // Section header row styling - dark gray background spanning all columns
          '& .MuiDataGrid-row.section-header': {
            backgroundColor: '#424242 !important',
            color: 'white !important',
            fontWeight: 'bold !important',
            border: 'none !important',
          },
          '& .MuiDataGrid-row.section-header .MuiDataGrid-cell': {
            backgroundColor: '#424242 !important',
            color: 'white !important',
            fontWeight: 'bold !important',
            border: 'none !important',
            borderRight: 'none !important',
            borderLeft: 'none !important',
            borderTop: 'none !important',
            borderBottom: 'none !important',
          },
          '& .MuiDataGrid-row.section-header:hover': {
            backgroundColor: '#424242 !important',
          },
          '& .MuiDataGrid-row.section-header .MuiDataGrid-cell:before': {
            display: 'none !important',
          },
          '& .MuiDataGrid-row.section-header .MuiDataGrid-cell:after': {
            display: 'none !important',
          },

          // Make header checkbox white
          '& .MuiDataGrid-columnHeader .MuiCheckbox-root': {
            color: 'var(--color-white)',
          },
          '& .MuiDataGrid-columnHeader .MuiCheckbox-root.Mui-checked': {
            color: 'var(--color-white)',
          },
          '& .MuiDataGrid-columnHeader .MuiCheckbox-root.Mui-indeterminate': {
            color: 'var(--color-white)',
          },
          // Make all row checkboxes black (#000000)
          '& .MuiDataGrid-cell .MuiCheckbox-root': {
            color: 'var(--color-text-secondary)',
          },
          '& .MuiDataGrid-cell .MuiCheckbox-root.Mui-checked': {
            color: 'var(--color-primary)',
          },
          '& .MuiDataGrid-cell .MuiCheckbox-root.Mui-indeterminate': {
            color: 'var(--color-text-secondary)',
          },
          // Ensure full height table body for scrolling
          '& .MuiDataGrid-autoHeight': {
            height: '100%',
          },
          '& .MuiDataGrid-footerContainer': {
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            backgroundColor: 'var(--color-white)',
            borderTop: '1px solid rgba(224, 224, 224, 1)',
          }
        }}
      />
      {columnVisibilityMenu}
      {savedViewsMenu}
      {saveViewDialog}
    </Paper>
  );
});

// Add display name for debugging
EnhancedDataTable.displayName = 'EnhancedDataTable';

export default EnhancedDataTable;
